import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

// Define the shape of our context
interface NavContextType {
  hidden: boolean;
  forceShown: boolean;
  setForceShown: (forceShown: boolean) => void;
  registerAnchor: (el: HTMLElement) => void;
  unregisterAnchor: (el: HTMLElement) => void;
  navTheme: "light" | "dark";
  isDarkNavTheme: boolean;
  isLightNavTheme: boolean;
  setNavTheme: (theme: "light" | "dark") => void;
  resetNavTheme: () => void;
  navAbsolute: boolean;
  setNavAbsolute: (absolute: boolean) => void;
  resetNavAbsolute: () => void;
}

// Create the context with default empty values
const NavContext = createContext<NavContextType>({
  hidden: false,
  forceShown: false,
  setForceShown: () => {},
  registerAnchor: () => {},
  unregisterAnchor: () => {},
  navTheme: "light",
  setNavTheme: () => {},
  resetNavTheme: () => {},
  isDarkNavTheme: false,
  isLightNavTheme: true,
  navAbsolute: false,
  setNavAbsolute: () => {},
  resetNavAbsolute: () => {},
});

interface NavProviderProps {
  children: ReactNode;
}

export const NavProvider: React.FC<NavProviderProps> = ({ children }) => {
  const [forceShown, setForceShown] = useState<boolean>(false);
  const [hidden, setHidden] = useState<boolean>(false);
  const [navTheme, setNavTheme] = useState<"light" | "dark">("light");
  const [navAbsolute, setNavAbsolute] = useState<boolean>(false);
  const anchorsRef = useRef<HTMLElement[]>([]);

  const registerAnchor = (el: HTMLElement) => {
    if (el && !anchorsRef.current.includes(el)) {
      anchorsRef.current.push(el);
    }
  };

  const unregisterAnchor = (el: HTMLElement) => {
    anchorsRef.current = anchorsRef.current.filter((a) => a !== el);
  };

  const resetNavTheme = useCallback(() => {
    setNavTheme("light");
  }, [setNavTheme]);

  const isLightNavTheme = useMemo(() => {
    return navTheme === "light";
  }, [navTheme]);

  const isDarkNavTheme = useMemo(() => {
    return navTheme === "dark";
  }, [navTheme]);

  const resetNavAbsolute = useCallback(() => {
    setNavAbsolute(false);
  }, [setNavAbsolute]);

  useEffect(() => {
    if (!setHidden) {
      return;
    }

    const onScroll = () => {
      const rects = anchorsRef.current.map((anchor) =>
        anchor.getBoundingClientRect()
      );

      const shouldHide = rects.some((rect) => rect.top < window.innerHeight);

      setHidden(shouldHide);
    };

    window.addEventListener("scroll", onScroll);
    onScroll(); // run once on mount

    return () => window.removeEventListener("scroll", onScroll);
  }, [setHidden]);

  return (
    <NavContext.Provider
      value={{
        hidden,
        registerAnchor,
        unregisterAnchor,
        navTheme,
        setNavTheme,
        resetNavTheme,
        isLightNavTheme,
        isDarkNavTheme,
        navAbsolute,
        setNavAbsolute,
        resetNavAbsolute,
        forceShown,
        setForceShown,
      }}
    >
      {children}
    </NavContext.Provider>
  );
};

// Custom hook to use the context
export const useNav = (): NavContextType => useContext(NavContext);
